import React from 'react';
import { Link } from 'gatsby';
import Icons from '../components/PageBlocks/Pricing/PricingIcons';
import Alt1 from '../components/icons/Alt1';

const papersignDiscount = {
  text: '',
  terms: '',
};

const enterpriseCTA = (
  <Link
    className="border-2 !text-midnight border-white hover:bg-white transition-all transition-200 p-1 px-2 rounded-lg text-[11px]"
    to="/pricing#enterprise"
  >
    Need more? Talk to Sales
  </Link>
);

function LabelWithDetails({ label, details, children }) {
  return (
    <>
      {label}
      <br />
      <span style={{ fontSize: '0.6rem' }}>{details}</span>
      {children ? <br /> : null}
      {children}
    </>
  );
}

const papersignFree = {
  id: 'free',
  title: 'Free',
  free: true,
  price: {
    monthly: 'Free',
    annual: 'Free',
    annualMonthly: 'Free',
  },
  discountPrice: '',
  discountPriceAnnual: '',
  discountPriceAnnualTotal: '',
  discountText: '',
  icon: Icons.Icon1,
  colorRGB: '241,223,213',
  tailwind: 'bg-[#F1DFD5] group-hover:bg-[#E4C3B2]',
  description:
    'For individuals and small teams just getting started with Papersign.',
  link: '/register/papersign?level=1',
  features: {
    signed_monthly_documents: 3,
    api_monthly_documents: false,
    users: 'Unlimited',
    upload_sign: true,
    rich_editor: true,
    activity_trail: true,
    tracking_notifications: true,
    email_chat_support: true,
    signed_document_certificate: true,
    paperform_integration: true,
    soc2: true,
    gdpr: true,
    reminders: 'Basic',
    consent_agreement: 'Default',
    document_organisation: 'Personal',
    papersign_api: false,
    bulk_sending: false,
    custom_email_notifications: false,
    signer_email_verification: false,
    manual_signing_links: false,
    webhooks: false,
  },
};

const papersignPro = {
  id: 'pro',
  title: 'Pro',
  free: false,
  price: {
    monthly: '$24',
    annual: '$240',
    annualMonthly: '$20',
  },
  discountPrice: '',
  discountPriceAnnual: '',
  discountPriceAnnualTotal: '',
  discountText: '', // discount.text,
  icon: Icons.Icon2,
  colorRGB: '255,200,78',

  tailwind: 'bg-[#FFC84E] group-hover:bg-[#FFB203]',
  description:
    'For organizations and businesses looking for multiple users or signed documents.',
  link: '/register/papersign?level=2',
  features: {
    soc2: true,
    gdpr: true,
    signed_monthly_documents: 'Unlimited*',
    users: 'Unlimited',
    api_monthly_documents: (
      <>
        10 per seat, per month
        <br />
        <small className="text-xs leading-tight opacity-70">
          Additional bundles available, see{' '}
          <a
            target="_blank"
            className="!text-black underline"
            href="/account/billing?product=papersign"
          >
            billing page
          </a>{' '}
          for details.
        </small>
      </>
    ),
    upload_sign: true,
    rich_editor: true,
    activity_trail: true,
    tracking_notifications: true,
    email_chat_support: true,
    signed_document_certificate: true,
    paperform_integration: true,
    reminders: 'Advanced',
    document_organisation: 'Team',
    consent_agreement: 'Custom',
    papersign_api: true,

    bulk_sending: true,
    custom_email_notifications: true,
    signer_email_verification: true,
    manual_signing_links: true,
    webhooks: true,
  },
};

const papersignEnterprise = {
  id: 'enterprise',
  title: 'Enterprise',
  enterprise: true,

  discountPrice: '',
  discountPriceAnnual: '',
  discountPriceAnnualTotal: '',
  discountText: '', // discount.text,
  icon: () => (
    <div className="p-1 h-36 w-36">
      <Alt1 color="black" />
    </div>
  ),
  colorRGB: '207,217,234',
  tailwind: 'bg-[#CFD9EA] group-hover:bg-[#A8B9D7]',
  description: 'For organizations with Enterprise or high volume needs.',
  link: '/register/papersign?level=2',
  features: {
    soc2: true,
    gdpr: true,
    signed_monthly_documents: 'Unlimited',
    users: 'Unlimited',
    api_monthly_documents: 'Unlimited',
    upload_sign: true,
    rich_editor: true,
    activity_trail: true,
    tracking_notifications: true,
    email_chat_support: true,
    signed_document_certificate: true,
    paperform_integration: true,
    papersign_api: true,
    dedicated_csm: true,
    multi_region: true,
    sla: true,
    sso: true,
    reminders: 'Advanced',
    document_organisation: 'Team',
    consent_agreement: 'Custom',
    papersign_api: true,

    bulk_sending: true,
    custom_email_notifications: true,
    signer_email_verification: true,
    manual_signing_links: true,
    webhooks: true,
  },
};

const paperformEssentials = {
  id: 'essentials',
  title: 'Essentials',
  icon: Icons.Icon1,
  free: false,
  price: {
    monthly: '$29',
    annual: '$288',
    annualMonthly: '$24',
  },
  colorRGB: '248,239,234',
  tailwind: 'bg-dusk-light/50 group-hover:bg-dusk-light/75',
  description: 'For individuals looking for a better way to build forms.',
  link: '/register?level=1',
  features: {
    // Plan comparison features
    forms: 'Unlimited',
    views: '10,000',
    users: 1,
    submissions: (
      <LabelWithDetails
        label="100 / month"
        details="+ $7.50/month/additional 250 (max 750)"
      />
    ),
    payment_submissions: '20',
    appointment_submissions: '50',
    appointments: true,
    submission_storage: '1,000',
    total_file_storage: '1GB',
    storage: '1,000 submissions',
    remove_branding: 'Not Removable',
    html_css: false,
    sso_addon: false,
    soc2: true,
    domains: false,
    // Other features
    payments: 'Yes',
    payment_accounts: 1,
    integration_count: 'Unlimited',
    support: 'Standard',
    max_file_size: '10MB / File',
    pagination: true,
    guided_mode: true,
    logic: true,
    required_conditional: true,
    scoring: true,
    autoclose_date: false,
    autoclose_number: true,
    form_sharing: true,
    multiple_calendars: false,
    calculations: true,
    fields: true,
    share: true,
    prefill: true,
    captcha: true,
    oembed: true,
    wordpress: true,
    save_resume: true,
    success_pages: true,
    dynamic_success_pages: false,
    answer_piping: true,
    closed_messaging: false,
    esignature: true,
    color_picker: true,
    templates: true,
    rich_media: true,
    giphy: true,
    image_editor: true,
    adobe: true,
    translation: true,
    advanced_themeing: false,
    form_url: false,
    integrations: true,
    results: true,
    export: true,
    notifications: true,
    emails: true,
    byo: true,
    spaces: true,
    redirects: true,
    third_party_analytics: true,
    custom_analytics: false,
    conditional_redirects: false,
    paperform_analytics: false,
    custom_pdfs: false,
    partial_submissions: false,
    api: false,
    webhooks: false,
    mult_user: false,
    products: true,
    price: true,
    pricing_rules: true,
    subscriptions: true,
    quotes: true,
    coupons: true,
    tax: true,
    stripe: true,
    gpay: true,
    braintree: true,
    paypal: true,
    square: true,
    refunds: true,
    _3dsecure: true,
    gdpr: true,
    ssl: true,
    twofa: true,
    enforce_2fa: false,
    byo_s3: false,
    google_address_search: false,
    external_options_list: false,
    annual_submissions: (
      <LabelWithDetails
        label="1,200 / year"
        details="+ $7.50/month/additional 3,000 (max 9,000)"
      />
    ),
    annual_payment_submissions: '240',
    annual_appointment_submissions: '600',
    ai_form_creation: true,
    ai_calculations: true,
    ai_report_insights: false,
  },
  highlights: [],
};

const paperformPro = {
  title: 'Pro',
  id: 'pro',
  icon: Icons.Icon2,
  description:
    'For small teams looking to keep forms on-brand and unlock our full functionality.',
  free: false,
  price: {
    monthly: '$59',
    annual: '$588',
    annualMonthly: '$49',
  },
  colorRGB: '241,223,213',
  tailwind: 'bg-[#F1DFD5] group-hover:bg-[#E4C3B2]',
  link: '/register?level=2',
  features: {
    ...paperformEssentials.features,
    // Plan comparison features
    forms: 'Unlimited',
    views: 'Unlimited',
    users: 3,
    submissions: (
      <LabelWithDetails
        label="1,000 / month"
        details="+ $10/month/additional 500 (max 2,500)"
      />
    ),
    payment_submissions: '250',
    appointment_submissions: '500',
    appointments: true,
    total_file_storage: '10 GB',
    mult_user: true,
    submission_storage: '10,000',
    storage: '10,000 submissions',
    remove_branding: 'Removable',
    html_css: true,
    sso_addon: false,
    domains: '+ $20 / month / domain',
    // Other features
    payment_accounts: 3,
    support: 'Priority',
    autoclose_date: true,
    autoclose_number: true,
    max_file_size: '1GB / File',
    multiple_calendars: true,
    calculations: true,
    dynamic_success_pages: true,
    answer_piping: true,
    closed_messaging: true,
    esignature: true,
    color_picker: true,
    advanced_themeing: true,
    form_url: true,
    custom_analytics: true,
    conditional_redirects: true,
    paperform_analytics: true,
    custom_pdfs: true,
    partial_submissions: true,
    api: true,
    webhooks: true,
    byo_s3: false,
    google_address_search: '+ $10 / month',
    external_options_list: false,
    annual_submissions: (
      <LabelWithDetails
        label="12,000 / year"
        details="+ $10/month/additional 6,000 (max 30,000)"
      />
    ),
    annual_payment_submissions: '3,000',
    annual_appointment_submissions: '6,000',
    ai_report_insights: true,
  },
  highlights: [],
};

const paperformBusiness = {
  title: 'Business',
  description:
    'For organizations and businesses after scalability, superior compliance and priority support.',
  free: false,
  price: {
    monthly: '$129',
    annual: '$1,188',
    annualMonthly: '$99',
  },
  // extraCTA: enterpriseCTA,
  id: 'business',
  icon: Icons.Icon3,
  colorRGB: '255,200,78',
  tailwind: 'bg-[#FFC84E] group-hover:bg-[#FFB203]',
  features: {
    ...paperformPro.features,
    // Plan comparison features
    users: <LabelWithDetails label={5} details="+ $9/month/user (max 25)" />,
    submissions: (
      <LabelWithDetails
        label="10,000 / month"
        details="+ $10/month/additional 1,000 (max 50,000)"
      />
    ),
    payment_submissions: '2,500',
    appointment_submissions: '5,000',
    appointments: true,
    total_file_storage: '100 GB',
    submission_storage: '100,000',
    storage: '10,000 submissions',
    remove_branding: 'Removable',
    html_css: true,
    sso_addon: true,
    business_api: true,
    domains: (
      <LabelWithDetails
        label="1 included"
        details="+ $20/month/additional domain"
      />
    ),
    // Other features
    payment_accounts: 'Unlimited',
    support: 'First Priority',
    max_file_size: '5GB / File',
    mult_user: true,
    enforce_2fa: true,
    byo_s3: true,
    google_address_search: true,
    external_options_list: true,
    annual_submissions: (
      <LabelWithDetails
        label="120,000 / year"
        details="+ $10/month/additional 120,000 (max 600,000)"
      />
    ),
    annual_payment_submissions: '30,000',
    annual_appointment_submissions: '60,000',
  },
  link: '/register?level=3',
  highlights: [],
};

const paperformEnterprise = {
  title: (
    <Link
      className="!text-black hover:bg-white transition bg-white/50 px-4 py-1 rounded-2xl"
      to="/enterprise/"
    >
      Enterprise
    </Link>
  ),
  description:
    'For organizations with Enterprise, bespoke, or high volume needs.',
  free: false,
  enterprise: true,
  // extraCTA: enterpriseCTA,
  id: 'enterprise',
  icon: () => (
    <div className="p-1 h-44 w-44">
      <Alt1 color="black" />
    </div>
  ),
  colorRGB: '207,217,234',
  tailwind: 'bg-[#CFD9EA] group-hover:bg-[#A8B9D7]',
  features: {
    ...paperformPro.features,
    // Plan comparison features
    users: 'Unlimited',
    submissions: 'Unlimited',
    payment_submissions: 'Unlimited',
    appointment_submissions: 'Unlimited',
    annual_submissions: 'Unlimited',
    annual_payment_submissions: 'Unlimited',
    annual_appointment_submissions: 'Unlimited',
    appointments: true,
    total_file_storage: 'Unlimited',
    submission_storage: 'Unlimited',
    storage: 'Unlimited',
    remove_branding: 'Removable',
    html_css: true,
    sso_addon: true,
    business_api: true,
    domains: 'Unlimited',
    // Other features
    payment_accounts: 'Unlimited',
    support: 'First Priority',
    max_file_size: 'Unlimited',
    mult_user: true,
    enforce_2fa: true,
    byo_s3: true,
    google_address_search: true,
    external_options_list: true,
    dedicated_csm: true,
    sla: true,
    local_data_residency: true,
    custom_permissions: true,
  },
  highlights: [],
};

const paperformPlus = {
  title: 'Paperform Agency+',
  description: '',
  free: false,
  id: 'plus',
  colorRGB: '255,200,78',
  tailwind: 'bg-[#FFC84E] group-hover:bg-[#FFB203]',
  features: {
    client_teams: (
      <>
        10 Pro client teams included.
        <br /> Additional teams for $19/month or $180/year.
      </>
    ),
    demo_teams: 'Unlimited demo teams',
    branding: true,
  },
  link: '/account/agency-plus',
  price: {
    monthly: '$190',
    annual: '$1,800',
    annualMonthly: '$150',
  },
};
export default {
  papersignDiscount,
  papersignFree,
  papersignPro,
  papersignEnterprise,
  paperformEssentials,
  paperformPro,
  paperformBusiness,
  paperformEnterprise,
  paperformPlus,
};
